import { Avatar, Box, Divider, Drawer, IconButton, Typography, Popover, Tooltip } from '@mui/material';
import Sidebar from '../Sidebar';
import Header from '../Header';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import '../../../assets/style/sass/custom.scss';
import Logo from '../../../assets/images/brand/dosen/logo.png';
import MotivLogo from '../../../assets/images/logo-motivhealth.png';
import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import ArticleIcon from '@mui/icons-material/Article';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { filterHiddenMemberAttributes } from '../utils/memberAttributes';
import { NO_ROLE_MESSAGE, hasNoRole } from '../utils/teamRoles';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import { HEADER_ELLIOTT_SLOT_ID } from '../../../components/layout/HeaderElliottSlot';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { IRoleObj, groupRoleListUpdate } from '../../../redux/slices/groupRolesListSlice';
import { updateSelectedId } from '../../../redux/slices/selectedGroupSlice';
import { setRoleMode } from '../../../redux/slices/roleModeSlice';
import { getUserDetails } from '../../../utils/orgName';
import ROLES from '../../../utils/roles';
import { useProgramContext } from '../../../hooks/useProgramContext';
import { breadCrumbsActions } from '../../../redux/slices/breadcrumbs/breadcrumbsSlice';
import { saveAnswerActions } from '../../../redux/slices/saveAnswersSlice';
import { setSelectedValue } from '../../../redux/slices/selectedValueSlice';
import { fetchMentorTasks } from '../../../redux/slices/mentorTasksSlice';
import { API } from '../../../api';
import { setOpenProgramIntent } from '../../../utils/openProgramIntent';
import { userInfoUpdate } from '../../../redux/slices/user-profile/userProfieSlice';
import MissingMandatoryFieldsTrigger from '../components/MissingMandatoryFieldsTrigger';
import _ from 'lodash';
import * as amplitude from '@amplitude/analytics-browser';
import { groupLoaderActions } from '../../../redux/slices/groupLoaderSlice';
import {
  AGILE_ORG,
  RDP_CREATIVE,
  RDP_CREATIVE_ORG_ID
} from '../../../utils/constants';
import useProfileDetails from '../../../hooks/useProfileDetails';
import { useLogout } from '../../../hooks';
import Intercom, { show as showIntercom } from '@intercom/messenger-js-sdk';
import { useModule } from '../../../utils/useModule';
import DosenButton from '../../../components/ui/Buttons/DosenButton';
import chatIcon from '../../../assets/images/chat_icon_3.svg';
import { Bone, fadeInSx } from '../../../components/ui/DashboardSkeletons';
import { useAdminAccess } from '../../../hooks/useAdminAccess';
import { canOpenCapabilitySetup } from '../tabs/capability-setup/access';
import { canOpenHiring, hiringEnabledForOrg } from '../tabs/hiring/access';
import useCapabilityRelease, { useCapabilityReleaseForMembers } from '../../../hooks/useCapabilityRelease';
import useNoRoleViewer from '../../../hooks/useNoRoleViewer';
import DosenTooltip from '../../../components/ui/DosenTooltip';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import { MOBILE_SX, isImmersiveRoute, useIsMobileApp } from '../../../mobile/mobileApp';
import { isRootRoute, isRouteActive, pickMobileTabs } from '../../../mobile/mobileTabs';
import MobileTabBar from '../../../mobile/MobileTabBar';
import PullToRefresh from '../../../mobile/PullToRefresh';
import useKeyboardOpen from '../../../mobile/useKeyboardOpen';

interface TabWithLockIconProps {
  tabName: string;
  isActive: boolean;
  onClick: () => void;
  tabDisable?: boolean;
  lockTooltip?: string;
}

interface NavItem {
  label: string | React.ReactNode;
  icon?: React.ReactNode;
  /** Drawn just right of the label (the learner's Elliott sparkle). */
  trailingIcon?: React.ReactNode;
  route?: string;
  disabled?: boolean;
  lockTooltip?: string;
  isSectionTitle?: boolean;
}

/* ===========================================================
   HEADER TITLE DINÂMICO PELO PATH
   =========================================================== */
function useDynamicHeaderTitle() {
  const location = useLocation();
  const selectedGroupName = useAppSelector((state) => state.selectedGroup.data?.name);
  // Capability Dashboard and Capability Setup are the Capability release's names (utils/capabilityRelease.ts).
  const capabilityRelease = useCapabilityRelease();
  // The Learning Framework dashboards (admin + member) and Learning Gaps are GA
  // for every org, so the chrome titles below no longer wait on the org details
  // fetch — they were only blank while the per-org flags were unknown.

  return useMemo(() => {
    const isMemberHomeRoute = /^\/app\/[hj]ome\/(mentee|mentor)\/?$/.test(location.pathname);
    if (isMemberHomeRoute) {
      return 'OBJECTIVES';
    }

    if (location.pathname.includes('/organizations')) return 'ORGANIZATIONS';
    // The page carries two panels — Learning Gaps and the Learning Framework —
    // so the chrome title is the broader "LEARNING DASHBOARD" ("CAPABILITY
    // DASHBOARD" with the Capability release).
    if (location.pathname.includes('/overview')) return capabilityRelease ? 'DASHBOARD' : 'LEARNING DASHBOARD';
    if (location.pathname.includes('/capability-setup')) return 'CAPABILITIES';
    if (location.pathname.includes('/app/hiring')) return 'HIRING';
    if (location.pathname.includes('/app/operations')) return 'CHECKS';
    if (location.pathname.match(/\/app\/org\/[^/]+\/members/)) return 'PEOPLE';
    if (location.pathname.includes('/users')) return 'PEOPLE';
    if (location.pathname.includes('/integrations')) return 'INTEGRATIONS';
    // Specific before generic: /profile/settings must be caught before the
    // /settings (PROGRAM MANAGEMENT) check below, which it otherwise matches.
    if (location.pathname.includes('/profile/settings')) return 'PROFILE SETTINGS';
    if (location.pathname.includes('/orgsettings')) return 'ORGANIZATION SETTINGS';
    if (location.pathname.includes('/settings')) return 'PROGRAM SETTINGS';
    if (location.pathname.includes('/insights')) return 'INSIGHTS';
    if (location.pathname.includes('/matches')) return 'MATCHES';
    if (location.pathname.includes('/calendar')) return 'CALENDAR';
    if (location.pathname.includes('/profile')) return 'PROFILE';
    if (location.pathname.includes('/library')) return 'RESOURCES';
    // Admin Resources lives at /app/resources (ContentSettings), distinct from
    // the learner /library route above.
    if (location.pathname.includes('/resources')) return 'RESOURCES';
    if (location.pathname.includes('/help')) return 'HELP';
    if (location.pathname.includes('/community')) return 'NETWORK';
    if (location.pathname.includes('/new-program/start')) return 'BUILD A PROGRAM';
    if (location.pathname.includes('/new-program/masterclass/step-2')) return 'BUILD A COURSE';
    if (location.pathname.includes('/new-program/masterclass/manual')) return 'BUILD A COURSE';
    if (location.pathname.includes('/new-program/mentorship')) return 'BUILD A MENTORSHIP PROGRAM';
    if (location.pathname.includes('/new-program/guided-masterclass/step-2')) return 'BUILD A GUIDED COURSE';
    if (location.pathname.includes('/new-program/guided-masterclass/manual')) return 'BUILD A GUIDED COURSE';
    if (location.pathname.includes('/designer')) return 'DESIGN';

    // Learner lesson view — show the program name (matches the classic path too;
    // "mentee-classic" doesn't contain "mentee/level" so it needs its own match).
    if (
      location.pathname.includes('/home/mentee/level') ||
      location.pathname.includes('/home/mentee-classic/level')
    ) {
      return selectedGroupName || 'DASHBOARD';
    }
    if (location.pathname.includes('/knowledge')) return 'KNOWLEDGE';
    if (location.pathname.includes('/charlie')) return 'ELLIOTT';
    if (location.pathname.includes('/chat')) return 'CHAT';
    if (location.pathname.includes('/home/user')) return 'DASHBOARD';
    if (location.pathname.includes('/aimd-v2')) return 'BUILD A PROGRAM';
    if (location.pathname.includes('/ai')) return 'BUILD A PROGRAM';

    return 'Elliott';
  }, [location.pathname, selectedGroupName, capabilityRelease]);
}

function RoleSwitch({
  isAdmin,
  disabled,
  allowAdmin,
  allowUser,
  onChange
}: {
  isAdmin: boolean;
  disabled: boolean;
  allowAdmin: boolean;
  allowUser: boolean;
  onChange: (target: 'admin' | 'user') => void;
}) {
  const learnerSelected = !isAdmin;
  const adminSelected = isAdmin;

  const tabBase = {
    paddingLeft: '18px',
    paddingRight: '18px',
    paddingTop: '9px',
    paddingBottom: '9px',
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    gap: '10px',
    display: 'flex',
    flex: 1,
    transition: 'background 0.15s',
  };

  const selectedTabStyle = { ...tabBase, background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)', cursor: disabled ? 'not-allowed' : 'pointer' };
  const defaultTabStyle = { ...tabBase, background: '#2A2A2A', cursor: disabled ? 'not-allowed' : 'pointer' };
  const selectedTextStyle = { color: '#FDFDFD', fontSize: 12, fontFamily: 'Inter', fontWeight: '700' as const };
  const defaultTextStyle = { color: '#B6B6B6', fontSize: 12, fontFamily: 'Inter', fontWeight: '400' as const };

  return (
    <Box sx={{ width: '100%', overflow: 'hidden', borderRadius: '6px', display: 'flex', opacity: disabled ? 0.7 : 1 }}>
      <Tooltip title={!allowUser ? 'You have not been invited as a Learner.' : ''}>
        <Box sx={learnerSelected ? selectedTabStyle : defaultTabStyle} onClick={() => { if (!disabled && allowUser) onChange('user'); }}>
          <Typography sx={learnerSelected ? selectedTextStyle : defaultTextStyle}>Learn</Typography>
        </Box>
      </Tooltip>
      <Tooltip title={!allowAdmin ? 'You have not been invited as an Admin.' : ''}>
        <Box sx={adminSelected ? selectedTabStyle : defaultTabStyle} onClick={() => { if (!disabled && allowAdmin) onChange('admin'); }}>
          <Typography sx={adminSelected ? selectedTextStyle : defaultTextStyle}>Manage</Typography>
        </Box>
      </Tooltip>
    </Box>
  );
}

/* ===========================================================
   DRAWER LATERAL — IGUAL AO FIGMA + NAVEGAÇÃO NOVA
   =========================================================== */
function ProgramSideDrawer({
  open,
  onOpen,
  onClose,
  onNavRoutes,
  onReselect
}: {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  /** Phone shell: the menu's destinations, so the header can tell a root screen from one opened from it. */
  onNavRoutes: (routes: string[]) => void;
  /** Phone shell: the tab of the current page was tapped again (back to the top of the page). */
  onReselect: () => void;
}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobileApp = useIsMobileApp();
  const keyboardOpen = useKeyboardOpen(isMobileApp);
  const { orgId, coId, isPlatformAdmin } = useProgramContext();
  const isOnProgramDetails = location.pathname.includes('/programdetails/');
  // The admin board (/programdetails/:orgId/:coId/admin/...), read from the URL itself. The Layout is a
  // parent route, so useProgramContext's groupId falls back to the persisted selected group there: after a
  // visit to Manage it read 'admin' on the learner dashboard too and pulled its menu over to Manage
  // (docs/BUG-learner-dashboard-shows-manage-menu.md).
  const onAdminBoardRoute = /\/programdetails\/[^/]+\/[^/]+\/admin(\/|$)/.test(location.pathname);
  const isPlatAdminOnProgram = isPlatformAdmin && isOnProgramDetails;

  const grpRoleListState = useAppSelector((state) => state.groupRoleList);
  const groupRoles = grpRoleListState.data || [];
  const selectedGroup = useAppSelector((state) => state.selectedGroup.data);
  const roleMode = useAppSelector((state) => state.roleMode.data);
  const userProfileData = useAppSelector((state) => state.userProfile.data);
  const oid = useAppSelector((state) => state.orgDetails.data?.oid);
  const reduxAuthOrgId = useAppSelector((state) => state.orgDetails.data?.authId);
  const authOrgId = reduxAuthOrgId || getUserDetails().orgId;
  // Org-level admin restructure is GA for every org — the admin Resources nav
  // item is always surfaced.
  const orgRealId = useAppSelector((state) => state.orgDetails.data?._id);
  const orgFlags = useAppSelector((state) => state.orgDetails.data?.flags);
  const [roleSwitching, setRoleSwitching] = useState(false);
  const { headshot, firstName, lastName } = useProfileDetails();
  // A member with no role in their team: an urgent mark on their profile, here and on the profile page.
  // Never on an org admin, nor for a team they manage (useNoRoleViewer; nothing until that is known).
  const forMembers = useCapabilityReleaseForMembers();
  const noRoleViewer = useNoRoleViewer();
  const noRole =
    forMembers &&
    noRoleViewer !== null &&
    hasNoRole(
      userProfileData?.teamRoles,
      filterHiddenMemberAttributes((userProfileData?.orgMemberAttributes ?? []) as any) as any,
      userProfileData?.memberAttributes ?? [],
      noRoleViewer
    );
  const { doLogout } = useLogout();

  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const userMenuOpen = Boolean(userMenuAnchor);

  /* ===========================================================
     Funções internas originais (não mexido)
     =========================================================== */

  const changeRoute = (roleObj: IRoleObj) => {
    dispatch(updateSelectedId(roleObj));
    if (location.pathname === '/app/profile/assessment' && roleObj.role === 'mentor') {
      navigate(`/app/profile/assessment`);
    } else {
      navigate(`/app/home/${roleObj.role}`);
    }
  };

  const dispatchBreadCrumbs = (id?: string) => {
    const grpId = id ? id : 'admin';
    dispatch(
      breadCrumbsActions.removeBreadCrumbs({
        id: grpId,
        name: 'Program',
        url: `/app/programdetails/${orgId}/${coId}/${grpId}/overview`
      })
    );
    dispatch(groupLoaderActions.updateState(true));
    navigate(`/app/programdetails/${orgId}/${coId}/${grpId}/overview`);
  };

  const redirectAdmin = (selectedObj: IRoleObj) => {
    dispatchBreadCrumbs(selectedObj?.id);
  };

  const switchToUser = async () => {
    const userTarget = getUserRoleTarget(); // já existe na sua lógica

    if (!userTarget) {
      navigate('/app/home/user');
      return;
    }

    setRoleSwitching(true);

    try {
      // 1 — atualizar lista de roles
      dispatch(groupRoleListUpdate.upadateState({ ...grpRoleListState, loader2: true }));
      const response = await API.groupsListForUser(orgId);

      // 2 — atualizar lastseen no backend
      try {
        const updateLastSeenResponse = await API.updateLastseen(userTarget);
        if (updateLastSeenResponse.status === 200) {
          dispatch(userInfoUpdate({ ...userProfileData, lastseen: userTarget }));
        }
      } catch (e) {
        // Best-effort background sync. Last-seen is already persisted to
        // localStorage and Redux, so a failed backend write must not
        // interrupt the user with an error toast. Log it for debugging.
        console.warn('Failed to persist last-seen program/role', e);
      }

      // 3 — atualizar redux group roles
      dispatch(
        groupRoleListUpdate.upadateState({
          loading: false,
          data: response?.data?.data || [],
          error: false,
          errroText: '',
          loader2: false
        })
      );

      // 4 — atualizar selectedId Redux
      dispatch(updateSelectedId({ ...userTarget, active: true }));

      // 5 — atualizar breadcrumbs
      dispatch(
        breadCrumbsActions.removeBreadCrumbs({
          id: userTarget.id,
          name: 'Program',
          url: `/app/programdetails/${orgId}/${coId}/${userTarget.id}/overview`
        })
      );

      // 6 — navegar
      navigate('/app/home/user');
    } catch (e) {
      console.error(e);
    } finally {
      setRoleSwitching(false);
    }
  };

  const updateLastSeenAndRoles = async (selectedObj: IRoleObj) => {
    try {
      dispatch(groupRoleListUpdate.upadateState({ ...grpRoleListState, loader2: true }));
      const response = await API.groupsListForUser(orgId);
      if (response.status === 200) {
        try {
          const updateLastSeenResponse = await API.updateLastseen(selectedObj);
          if (updateLastSeenResponse.status === 200) {
            dispatch(userInfoUpdate({ ...userProfileData, lastseen: selectedObj }));
          }
        } catch (e) {
          // Best-effort background sync — see switchToUser. Failing quietly
          // avoids a disruptive error toast for a non-blocking write.
          console.warn('Failed to persist last-seen program/role', e);
        }

        dispatch(
          groupRoleListUpdate.upadateState({
            loading: false,
            data: response.data.data || [],
            error: false,
            errroText: '',
            loader2: false
          })
        );
      }
    } catch (e) {
      dispatch(
        groupRoleListUpdate.upadateState({
          loading: false,
          data: undefined,
          error: true,
          errroText: _.get(e, 'response.data.message', `Failed to get user group role list`),
          loader2: false
        })
      );
    }
  };

  /* ===========================================================
     SWITCH DE ROLE — original
     =========================================================== */

  const allowedUserRoles = isPlatformAdmin
    ? []
    : groupRoles.filter((each) => {
        if (each.role === 'admin') return false;
        if (each.role === 'mentee' && oid === AGILE_ORG) return false;
        return true;
      });

  const getUserRoleTarget = (): IRoleObj | undefined => {
    if (isPlatformAdmin) return undefined;
    const selectedMatch = allowedUserRoles.find(
      (each) => each.id === selectedGroup.id && each.role === selectedGroup.role
    );

    const lastSeenFromStorage = (() => {
      try {
        return JSON.parse(localStorage.getItem('lastseen') || '');
      } catch (error) {
        return undefined;
      }
    })();

    const lastSeenMatch = lastSeenFromStorage
      ? allowedUserRoles.find((each) => each.id === lastSeenFromStorage.id && each.role === lastSeenFromStorage.role)
      : undefined;

    const profileLastSeen = userProfileData?.lastseen;
    const profileMatch = profileLastSeen
      ? allowedUserRoles.find((each) => each.id === profileLastSeen.id && each.role === profileLastSeen.role)
      : undefined;

    return selectedMatch || lastSeenMatch || profileMatch || allowedUserRoles[0];
  };

  const adminRole = isPlatformAdmin ? undefined : groupRoles.find((each) => each.role === 'admin');
  // A team manager is an admin of their team's programs, so they normally have
  // an admin group role. Until their team has a program they have none — but
  // their team's Learning Gaps still make an admin side.
  // A team manager with no admin program in the role list still gets the
  // admin side (their team's programs may not exist yet).
  const { access: adminAccess } = useAdminAccess();
  const teamManagerOnly = !isPlatformAdmin && !adminRole && (adminAccess?.managedTeamIds.length ?? 0) > 0;
  // The Capability release (utils/capabilityRelease.ts) renames the dashboard and adds Capability Setup.
  const capabilityRelease = useCapabilityRelease();
  const dashboardLabel = capabilityRelease ? 'Dashboard' : 'Learning Framework';
  // Capability Setup is for org admins and team managers (the backend's rule):
  // a program admin who manages no team doesn't get it.
  const canSetUpCapabilities =
    capabilityRelease && canOpenCapabilitySetup(adminAccess, isPlatformAdmin, localStorage.getItem('role') || '');
  // Hiring (DA-5042 H-1) is its own top-level section, separate from the learner
  // and admin areas, because it is a sandbox: nothing a candidate does reaches
  // the org's scores, insights or digests (PRD 6.2), and nothing in the product
  // reaches a candidate (6.1, C-9). It shows only for an org that has been given
  // the feature, and only for whoever may run a round.
  const canOpenHiringSection =
    hiringEnabledForOrg({ flags: orgFlags }) &&
    canOpenHiring(adminAccess, isPlatformAdmin, localStorage.getItem('role') || '');
  const hiringNavItems: NavItem[] = canOpenHiringSection
    ? [
        { label: 'Hiring Toolkit', isSectionTitle: true },
        { label: 'Hiring', route: '/app/hiring' }
      ]
    : [];
  // Org-level invitation roles (users.orgRoles): a learner invited without a
  // program has zero group memberships until (auto-)enrolment, so membership
  // alone can't distinguish them from an admin-only invitee — the Learner
  // toggle must also honour the persisted org-level role.
  const orgInvitedAsLearner =
    !isPlatformAdmin &&
    (userProfileData?.orgRoles || []).some((r) => {
      if (r === 'mentee' && oid === AGILE_ORG) return false;
      return r === 'mentee' || r === 'mentor';
    });
  const hasUserRole = allowedUserRoles.length > 0 || orgInvitedAsLearner;
  // Admin-toolkit routes carry no /admin/ URL segment and set no selectedGroup
  // role, which used to flip the toggle back to Learner when an admin opened
  // Knowledge / Settings / Members etc. — even for users who aren't learners.
  const onAdminToolkitRoute =
    /^\/app\/(knowledge|capability-setup|operations|orgsettings|integrations|resources|organizations)/.test(location.pathname) ||
    /^\/app\/org\/[^/]+\/members/.test(location.pathname);
  const hasAdminSide = !!adminRole || teamManagerOnly;
  // Definitive URL signals: learner dashboards / library are Learner; /admin/
  // program routes, a fresh program page with no role yet, and toolkit routes
  // are Admin.
  const onLearnerRoute = /^\/app\/(home\/(user|mentee|mentor)|library)(\/|$)/.test(location.pathname);
  const urlSaysAdmin =
    onAdminBoardRoute ||
    (isOnProgramDetails && !!adminRole && !selectedGroup?.role) ||
    (hasAdminSide && onAdminToolkitRoute);
  // Nothing to toggle back to: always Admin.
  const pureAdmin = !hasUserRole && hasAdminSide;

  // Admin toggle. URL signals win; shared routes (Chat, Calendar, Network,
  // Elliott, Profile, Help) keep the last recorded roleMode, so the toggle
  // doesn't flip there or on refresh; with nothing recorded, the selected
  // group's role decides.
  const resolveAdminSelected = (): boolean => {
    if (isPlatAdminOnProgram) return true;
    if (isPlatformAdmin) return false;
    if (pureAdmin) return true;
    if (urlSaysAdmin) return true;
    if (onLearnerRoute) return false;
    if (roleMode === 'admin') return hasAdminSide; // ignore stale intent w/o an admin side
    if (roleMode === 'user') return false;
    return selectedGroup?.role === 'admin';
  };
  const isAdminSelected = resolveAdminSelected();

  const charlieBlockedOrgs = [
    'org_LGe1jsJ5OwlEUG0p', // EBRD
    'org_XkihyfFNYhtLPB1t', // Group Effort
    'org_4eT4RL6dY0Ftymdz'  // Pepperdine
  ];
  const showCharlie = !charlieBlockedOrgs.includes(orgId);

  // Members is an admin-only surface — admins, org admins and platform admins
  // all get it; learners and mentors never do (it isn't in userNavItems).
  const platformAdminNavItems: NavItem[] = [
    { label: 'Organizations', route: '/app/organizations' },
    { label: 'Elliott Conversations', route: '/app/elliott-conversations' },
    { label: 'Program Build Time', route: '/app/course-creation-time' },
    ...(authOrgId ? [{ label: 'People', route: `/app/org/${authOrgId}/members` }] : []),
    ...(showCharlie ? [{ label: 'Elliott', route: '/app/charlie' }] : []),
    ...(showCharlie ? [{ label: 'Knowledge', route: '/app/knowledge' }] : []),
    { label: 'Chat', route: '/app/chat' },
    { label: 'Calendar', route: '/app/calendar' },
    { label: 'Network', route: '/app/community' }
  ];

  // The menu before the Capability release: one Admin Toolkit, then Communication.
  const legacyPlatformAdminProgramItems: NavItem[] = [
    { label: dashboardLabel, route: `/app/programdetails/${orgId}/${coId}/admin/overview` },
    ...(authOrgId ? [{ label: 'People', route: `/app/org/${authOrgId}/members` }] : []),
    ...(showCharlie ? [{ label: 'Elliott', route: '/app/charlie' }] : []),
    ...(showCharlie ? [{ label: 'Knowledge', route: '/app/knowledge' }] : []),
    // Managing an org: give platform admins the same admin toolkit (scoped to
    // the managed org via orgId/coId/localStorage) so they can edit settings,
    // integrations, insights and resources — not just view the framework.
    { label: 'Resources', route: '/app/resources' },
    { label: 'Insights', route: `/app/programdetails/${orgId}/${coId}/admin/insights-v3` },
    { label: 'Integrations', route: '/app/integrations' },
    { label: 'Settings', route: '/app/orgsettings' },
    { label: 'Chat', route: '/app/chat' },
    { label: 'Calendar', route: '/app/calendar' },
    { label: 'Network', route: '/app/community' }
  ];
  const legacyAdminNavItems: NavItem[] = [
    // Hiring doesn't depend on the Capability release, so the older menu gets it too.
    ...hiringNavItems,
    { label: 'Admin Toolkit', isSectionTitle: true },
    { label: dashboardLabel, route: `/app/programdetails/${orgId}/${coId}/admin/overview` },
    ...(canSetUpCapabilities ? [{ label: 'Capabilities', route: '/app/capability-setup' }] : []),
    ...(authOrgId ? [{ label: 'People', route: `/app/org/${authOrgId}/members` }] : []),
    { label: 'Knowledge', route: '/app/knowledge' },
    { label: 'Resources', route: '/app/resources' },
    { label: 'Insights', route: `/app/programdetails/${orgId}/${coId}/admin/insights-v3` },
    // Org-only pages (ProfileOrgSettingsGuard sends a manager to /unauthorized).
    ...(teamManagerOnly
      ? []
      : [
          { label: 'Integrations', route: '/app/integrations' },
          { label: 'Settings', route: '/app/orgsettings' }
        ]),
    { label: 'Communication Toolkit', isSectionTitle: true },
    { label: 'Chat', route: '/app/chat' },
    { label: 'Calendar', route: '/app/calendar' },
    { label: 'Network', route: '/app/community' }
  ];

  /**
   * The Capability release's admin menu, in three toolkits: Capability
   * (Dashboard, Members, Insights, Capabilities, Operations), Configuration (Knowledge,
   * Integrations, Resources, Settings) and Communication (Chat, Calendar,
   * Network). Capabilities and Operations are for whoever may set capabilities up; Integrations and
   * Settings are org-only pages a team manager doesn't get.
   */
  const capabilityNavItems = (show: {
    setup: boolean;
    orgPages: boolean;
    knowledge: boolean;
    extraConfiguration: NavItem[];
  }): NavItem[] => [
    { label: 'Capability Toolkit', isSectionTitle: true },
    { label: 'Dashboard', route: `/app/programdetails/${orgId}/${coId}/admin/overview` },
    ...(authOrgId ? [{ label: 'People', route: `/app/org/${authOrgId}/members` }] : []),
    { label: 'Insights', route: `/app/programdetails/${orgId}/${coId}/admin/insights-v3` },
    // Capabilities (Capability Setup) and Operations are for whoever may set capabilities up.
    ...(show.setup
      ? [
          { label: 'Capabilities', route: '/app/capability-setup' },
          { label: 'Checks', route: '/app/operations' }
        ]
      : []),
    ...hiringNavItems,
    { label: 'Configuration Toolkit', isSectionTitle: true },
    ...show.extraConfiguration,
    ...(show.knowledge ? [{ label: 'Knowledge', route: '/app/knowledge' }] : []),
    ...(show.orgPages ? [{ label: 'Integrations', route: '/app/integrations' }] : []),
    { label: 'Resources', route: '/app/resources' },
    ...(show.orgPages ? [{ label: 'Settings', route: '/app/orgsettings' }] : []),
    { label: 'Communication Toolkit', isSectionTitle: true },
    { label: 'Chat', route: '/app/chat' },
    { label: 'Calendar', route: '/app/calendar' },
    { label: 'Network', route: '/app/community' }
  ];

  const platformAdminProgramNavItems: NavItem[] = [
    { label: 'Organizations', route: '/app/organizations' },
    { label: 'Elliott Conversations', route: '/app/elliott-conversations' },
    { label: 'Program Build Time', route: '/app/course-creation-time' },
    ...(capabilityRelease
      ? capabilityNavItems({
          setup: true,
          orgPages: true,
          extraConfiguration: showCharlie ? [{ label: 'Elliott', route: '/app/charlie' }] : [],
          knowledge: showCharlie
        })
      : legacyPlatformAdminProgramItems)
  ];

  const adminNavItems: NavItem[] = capabilityRelease
    ? capabilityNavItems({ setup: canSetUpCapabilities, orgPages: !teamManagerOnly, extraConfiguration: [], knowledge: true })
    : legacyAdminNavItems;

  const isRdpCreative = oid === RDP_CREATIVE || orgRealId === RDP_CREATIVE_ORG_ID;

  // Group Effort Initiative: Resources are admin-only — learners don't get the
  // library in their nav (go-live request, July 2026).
  const isGroupEffort =
    orgId === 'org_XkihyfFNYhtLPB1t' || oid === 'org_XkihyfFNYhtLPB1t' || orgRealId === '6938931e8797d2e278fe1c38';

  const userNavItems: NavItem[] = [
    { label: 'Learner Toolkit', isSectionTitle: true },
    { label: 'Dashboard', route: '/app/home/user' },
    ...(showCharlie
      ? [
          {
            label: 'Elliott',
            route: '/app/charlie',
            trailingIcon: <AutoAwesomeIcon aria-hidden sx={{ fontSize: 16, color: '#F79420' }} />
          }
        ]
      : []),
    ...(isGroupEffort ? [] : [{ label: 'Resources', route: '/app/library' }]),
    { label: 'Communication Toolkit', isSectionTitle: true },
    { label: 'Chat', route: '/app/chat' },
    ...(isRdpCreative
      ? []
      : [
          { label: 'Calendar', route: '/app/calendar' },
          { label: 'Network', route: '/app/community' }
        ])
  ];

  const [localRole, setLocalRole] = useState<'admin' | 'user'>(isAdminSelected ? 'admin' : 'user');
  const getNavItems = () => {
    if (isPlatAdminOnProgram) return platformAdminProgramNavItems;
    if (isPlatformAdmin) return platformAdminNavItems;
    if (localRole === 'admin') return adminNavItems;
    return userNavItems;
  };
  const navItems = getNavItems();

  // Phone shell: the header tells menu destinations (no back button) from the
  // screens opened from them, and the bottom tabs are the menu's main entries.
  const navRoutesKey = navItems.flatMap((item) => (item.route && !item.isSectionTitle ? [item.route] : [])).join('|');
  useEffect(() => {
    onNavRoutes(navRoutesKey ? navRoutesKey.split('|') : []);
  }, [navRoutesKey, onNavRoutes]);

  useEffect(() => {
    if (!isPlatformAdmin) {
      setLocalRole(isAdminSelected ? 'admin' : 'user');
    }
  }, [isAdminSelected, isPlatformAdmin]);

  // Record roleMode from URL signals only; the shared-route fallback reads it.
  useEffect(() => {
    if (isPlatformAdmin) return;
    if (urlSaysAdmin) dispatch(setRoleMode('admin'));
    else if (onLearnerRoute) dispatch(setRoleMode('user'));
  }, [urlSaysAdmin, onLearnerRoute, isPlatformAdmin, dispatch]);

  const handleRoleToggle = (target: 'admin' | 'user') => {
    if (isPlatformAdmin || roleSwitching) return;
    setLocalRole(target);
    dispatch(setRoleMode(target));

    if (target === 'user') {
      const userTarget = getUserRoleTarget();
      if (userTarget) {
        dispatch(updateSelectedId({ ...userTarget, active: true }));
        dispatch(
          breadCrumbsActions.removeBreadCrumbs({
            id: userTarget.id,
            name: 'Program',
            url: `/app/programdetails/${orgId}/${coId}/${userTarget.id}/overview`
          })
        );
      }
      // Navigate immediately — the learner dashboard fetches its own data on mount,
      // so there is no need to block the switch on the role-list refresh.
      navigate('/app/home/user');
      onClose();
      // Sync group roles / last-seen in the background (non-blocking, no loader).
      if (userTarget) {
        const syncUserRoles = async () => {
          try {
            const response = await API.groupsListForUser(orgId);
            dispatch(groupRoleListUpdate.upadateState({ loading: false, data: response?.data?.data || [], error: false, errroText: '', loader2: false }));
            const updateLastSeenResponse = await API.updateLastseen(userTarget);
            if (updateLastSeenResponse.status === 200) {
              dispatch(userInfoUpdate({ ...userProfileData, lastseen: userTarget }));
            }
          } catch (e) {
            // Explicitly a non-blocking background sync (see comment above);
            // never surface it as an error toast to the user.
            console.warn('Failed to sync roles / persist last-seen', e);
          }
        };
        syncUserRoles();
      }
      return;
    }

    // target === 'admin'
    const nextRole = adminRole || { id: 'admin', name: 'Admin', role: 'admin', active: true };
    dispatch(updateSelectedId({ ...nextRole, active: true }));
    dispatch(groupLoaderActions.updateState(true));
    navigate(`/app/programdetails/${orgId}/${coId}/admin/overview`);
    onClose();
    // Persist last-seen / refresh roles in the background.
    updateLastSeenAndRoles(nextRole);
  };

  useEffect(() => {
    if (isPlatformAdmin && !isPlatAdminOnProgram) return;
    if (onAdminBoardRoute && selectedGroup?.role !== 'admin') {
      const adminSelection = isPlatAdminOnProgram
        ? { id: 'admin', name: 'Admin', role: 'admin', active: true }
        : adminRole || { id: 'admin', name: 'Admin', role: 'admin', active: true };
      dispatch(updateSelectedId({ ...adminSelection, active: true }));
    }
  }, [onAdminBoardRoute, adminRole, dispatch, selectedGroup?.role, isPlatformAdmin, isPlatAdminOnProgram]);

  /** Opens a menu destination; the menu list and the phone's bottom tabs both go through here. */
  const openRoute = (route: string) => {
    if (route.includes('/programdetails/')) {
      dispatch(groupLoaderActions.updateState(true));
      if (route.includes('/admin/')) {
        const adminSelection = adminRole || { id: 'admin', name: 'Admin', role: 'admin', active: true };
        dispatch(updateSelectedId({ ...adminSelection, active: true }));
      }
    }
    navigate(route);
  };

  // Phone shell: the bottom tabs. Hidden on full-screen task pages and while the keyboard is up.
  const mobileTabs = isMobileApp ? pickMobileTabs(navItems) : [];
  const activeTab = mobileTabs.find((tab) => isRouteActive(tab.route, location.pathname));
  const onOtherMenuDestination = !activeTab && navItems.some((item) => !!item.route && isRouteActive(item.route, location.pathname));
  const showTabBar = mobileTabs.length > 0 && !isImmersiveRoute(location.pathname) && !keyboardOpen;
  // The phone's way to support: the floating Intercom launcher is off there (it sat on the tabs).
  const showSupport = isMobileApp && !!process.env.REACT_APP_INTERCOMM_APP_ID;

  /* ===========================================================
     RENDER DO DRAWER
     =========================================================== */

  return (
    <>
    {showTabBar && (
      // Rendered ahead of the header (this component's effects keep their old
      // order); `order` puts the bar at the bottom of the layout's column.
      <Box sx={{ order: 1, flexShrink: 0 }}>
        <MobileTabBar
          tabs={mobileTabs}
          activeRoute={activeTab?.route}
          menuActive={open || onOtherMenuDestination}
          onSelect={(route) => (isRootRoute(route, location.pathname) ? onReselect() : openRoute(route))}
          onMenu={onOpen}
        />
      </Box>
    )}
    <Drawer
      open={open}
      onClose={onClose}
      anchor="left"
      PaperProps={{
        sx: {
          width: 300,
          bgcolor: '#0F0F0F',
          borderRight: '1px solid #2A2A2A',
          p: 1.25,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          [MOBILE_SX]: {
            width: 'min(86vw, 320px)',
            pt: 'calc(10px + env(safe-area-inset-top))',
            pb: 'calc(10px + env(safe-area-inset-bottom))'
          }
        }
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          p: 1.25,
          background: '#0F0F0F',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
          <Box sx={{ py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconButton onClick={onClose} size="small">
              <CloseIcon sx={{ color: 'white' }} fontSize="large" />
            </IconButton>
          </Box>
        </Box>

        {/* Role Toggle */}
        {!isPlatformAdmin && (
          <Box sx={{ px: 0.5, pb: 1 }}>
            <RoleSwitch
              isAdmin={localRole === 'admin'}
              disabled={roleSwitching}
              allowAdmin={hasAdminSide}
              allowUser={hasUserRole}
              onChange={handleRoleToggle}
            />
          </Box>
        )}

        {/* Menu */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, overflowY: 'auto', pr: 0.5 }}>
          {navItems.map((item, idx) => {
            if (item.isSectionTitle) {
              // Section groups show as a divider line only (no heading text);
              // the first group needs no line under the role toggle.
              if (idx === 0) return null;
              return <Divider key={idx} sx={{ my: 1.5, borderColor: '#2A2A2A' }} />;
            }

            const content = (
              <Box
                sx={{
                  height: 44,
                  py: 1.1,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  cursor: item.disabled ? 'not-allowed' : 'pointer',
                  '&:hover': item.disabled ? {} : { background: '#1A1A1A' }
                }}
                onClick={() => {
                  if (item.route && !item.disabled) {
                    openRoute(item.route);
                    onClose();
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  {item.icon ? (
                    <Box sx={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {item.icon}
                    </Box>
                  ) : null}
                  <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 400, textTransform: 'uppercase', fontFamily: 'Inter, sans-serif' }}>
                    {item.label}
                  </Typography>
                  {item.trailingIcon}
                </Box>
              </Box>
            );

            const showDivider = (isPlatformAdmin && !isPlatAdminOnProgram) && idx < navItems.length - 1;

            return (
              <Box key={item.route || idx}>
                <Tooltip title={item.lockTooltip || ''} disableHoverListener={!item.lockTooltip}>
                  <span style={{ width: '100%', display: 'block' }}>{content}</span>
                </Tooltip>
                {showDivider ? <Divider sx={{ my: 1, borderColor: '#444444' }} /> : null}
              </Box>
            );
          })}
          {showSupport && (
            <>
              <Divider sx={{ my: 1.5, borderColor: '#2A2A2A' }} />
              <Box
                data-testid="mobile-menu-support"
                onClick={() => {
                  onClose();
                  showIntercom();
                }}
                sx={{ height: 44, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
              >
                <SupportAgentOutlinedIcon sx={{ color: '#B6B6B6', fontSize: 20 }} />
                <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 400, textTransform: 'uppercase', fontFamily: 'Inter, sans-serif' }}>
                  Support
                </Typography>
              </Box>
            </>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 'auto' }}>
          <Box
            sx={{
              width: '100%',
              p: 1.25,
              borderRadius: 0.8,
              outline: '1px solid #2A2A2A',
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DosenTooltip title={noRole ? NO_ROLE_MESSAGE : ''} placement="right">
                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                  <Avatar
                    src={headshot}
                    sx={{ width: 40, height: 40, borderRadius: '999px', background: '#1C1C1C', cursor: 'pointer' }}
                    onClick={() => {
                      navigate('/app/profile');
                      onClose();
                    }}
                  />
                  {noRole && (
                    <Box
                      data-testid="no-role-nav-flag"
                      aria-label={NO_ROLE_MESSAGE}
                      sx={{
                        position: 'absolute',
                        top: -3,
                        right: -3,
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        bgcolor: '#E5484D',
                        border: '2px solid #0F0F0F',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none'
                      }}
                    >
                      <PriorityHighIcon sx={{ fontSize: 10, color: '#FDFDFD' }} />
                    </Box>
                  )}
                </Box>
              </DosenTooltip>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
                  {firstName} {lastName}
                </Typography>
                <Typography sx={{ color: '#444444', fontSize: 12, fontWeight: 500, fontFamily: 'Inter, sans-serif' }}>
                  User
                </Typography>
              </Box>
              <Box>
                <IconButton
                  size="small"
                  onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                  sx={{
                    color: '#FDFDFD',
                    width: 28,
                    height: 28
                  }}
                >
                  <MoreHorizIcon fontSize="small" />
                </IconButton>
                <Popover
                  open={userMenuOpen}
                  anchorEl={userMenuAnchor}
                  onClose={() => setUserMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{
                    sx: {
                      bgcolor: '#0F0F0F',
                      border: '1px solid #2A2A2A',
                      mt: 1
                    }
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#1C1C1C' }
                    }}
                    onClick={() => {
                      doLogout();
                      setUserMenuAnchor(null);
                    }}
                  >
                    <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 600 }}>Logout</Typography>
                  </Box>
                </Popover>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Drawer>
    </>
  );
}

/* ===========================================================
   HEADER PRINCIPAL (FIGMA)
   =========================================================== */
type NotificationSummary = {
  unreadMessagesCount?: number;
  upcomingMeetingsCount?: number;
  pendingMatchesCount?: number;
  /** Unread stored `program_updated` items (course versioning publishes). */
  programUpdatesCount?: number;
  /** Unread stored `program_assigned` items (enrolled in a program by its audience). */
  programAssignedCount?: number;
  /** Unread stored `collaborator_invite` items (co-design invites). */
  programInvitesCount?: number;
  /** Unread stored `practice_nudge` items (an admin's Encourage Practice). */
  practiceNudgesCount?: number;
  /** Unread stored `member_nudge` items (an admin's nudge from Org Members). */
  memberNudgesCount?: number;
  /** Unread stored `role_not_listed` items (a member of a team they manage says their role isn't listed). */
  roleNotListedCount?: number;
};
type NotificationActor = {
  userId?: string;
  mid?: string;
  communicationId?: string;
  displayName?: string;
  headshot?: string;
};
type NotificationItem = {
  type: string;
  title: string;
  text: string;
  count?: number;
  actors?: NotificationActor[];
  /** Stored items (`program_updated`, `collaborator_invite`, `practice_nudge`, `member_nudge`) carry their id so a click can mark them read. */
  notificationId?: string;
  /** `program` (course versioning publish) carries the program to open;
   *  `program_build` (co-design invite) the program whose builder to open. */
  cta?: { label: string; screen: string; grpId?: string; programId?: string; teamId?: string };
};
/** The summary count each kind of stored item is counted under: opening one takes it off at once. */
const STORED_COUNT: Partial<Record<string, keyof NotificationSummary>> = {
  program_updated: 'programUpdatesCount',
  program_assigned: 'programAssignedCount',
  collaborator_invite: 'programInvitesCount',
  practice_nudge: 'practiceNudgesCount',
  member_nudge: 'memberNudgesCount',
  role_not_listed: 'roleNotListedCount'
};
type NotificationsResponse = {
  summary: NotificationSummary;
  notifications: NotificationItem[];
  generatedAt?: number;
};

function getNotificationNavigateTo(n: NotificationItem): { path: string; grpId?: string; state?: { teamId: string } } {
  const screen = n.cta?.screen || 'mentorship';
  if (screen === 'program' && n.cta?.grpId) return { path: '/app/home/user', grpId: n.cta.grpId };
  if (screen === 'messages' && n.actors?.length) {
    const first = n.actors[0];
    const commId = first.communicationId || first.mid || '';
    const name = first.displayName || '';
    return { path: `/app/chat/?commId=${encodeURIComponent(commId)}&name=${encodeURIComponent(name)}&type=Chats` };
  }
  if (screen === 'meetings') return { path: '/app/calendar' };
  // An admin's nudge: it is about the member's profile.
  if (screen === 'profile') return { path: '/app/profile' };
  // Co-design invite: the admin dashboard opens the program's builder.
  if (screen === 'program_build') return { path: '/app/redirect' };
  // "My role isn't listed": Capabilities, on the member's team.
  if (screen === 'capabilities') {
    return { path: '/app/capability-setup', ...(n.cta?.teamId ? { state: { teamId: n.cta.teamId } } : {}) };
  }
  if (screen === 'mentorship') return { path: '/app/home/user' };
  return { path: '/app/home/user' };
}

function ProgramHeader({
  onOpenMenu,
  rootScreen,
  homeRoute
}: {
  onOpenMenu: () => void;
  /** Phone shell: the page is a menu destination (logo on the left) rather than a screen opened from one (back button). */
  rootScreen: boolean;
  /** Phone shell: where back goes when the app was opened straight on an inner screen. */
  homeRoute: string;
}) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const isMobileApp = useIsMobileApp();
  const groupRoles = useAppSelector((state) => state.groupRoleList.data || []);
  const title = useDynamicHeaderTitle();
  const orgLogo = useAppSelector((state) => state.orgDetails.data?.orgLogo);
  // Until the org's details load we don't know its logo, so hold the space
  // instead of showing the Dosen logo and swapping it a moment later.
  const orgLogoResolved = useAppSelector((state) => state.orgDetails.data !== undefined || state.orgDetails.error);
  const selectedGroup = useAppSelector((state) => state.selectedGroup.data);
  const { orgId, location: userLocation } = useProgramContext();
  const headerLogo = orgId === 'org_AonfhEWK36h2g0KM' ? MotivLogo : orgLogo || Logo;

  const [notifAnchorEl, setNotifAnchorEl] = useState<HTMLElement | null>(null);
  const [notifData, setNotifData] = useState<NotificationsResponse | null>(null);
  const [notifLoading, setNotifLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!orgId) return;
    const tokens = localStorage.getItem('tokens') ? JSON.parse(localStorage.getItem('tokens') || '{}') : {};
    const idToken = tokens.id_token;
    const accessToken = tokens.access_token;
    if (!idToken || !accessToken) return;
    setNotifLoading(true);
    try {
      const res = await API.getMemberNotifications({
        orgId,
        idtoken: idToken,
        authorization: accessToken,
        location: userLocation
      });
      const data = res?.data?.data;
      if (data) setNotifData({ summary: data.summary || {}, notifications: data.notifications || [], generatedAt: data.generatedAt });
      else setNotifData({ summary: {}, notifications: [] });
    } catch (e) {
      setNotifData({ summary: {}, notifications: [] });
    } finally {
      setNotifLoading(false);
    }
  }, [orgId, userLocation]);

  // A "program updated" bell entry (course versioning) opens that program as
  // a learner: select the membership (mentee first), remember it, go home.
  const openProgramFromNotification = useCallback(
    (grpId: string) => {
      const entry =
        (groupRoles as IRoleObj[]).find((g) => g.id === grpId && g.role === 'mentee') ||
        (groupRoles as IRoleObj[]).find((g) => g.id === grpId);
      if (!entry) {
        navigate('/app/home/user');
        return;
      }
      dispatch(updateSelectedId({ ...entry, active: true }));
      dispatch(setSelectedValue(`${entry.id}_${entry.role}`));
      try {
        localStorage.setItem('lastseen', JSON.stringify(entry));
      } catch {
        /* storage blocked: Redux already holds the selection */
      }
      Promise.resolve(API.updateLastseen({ id: entry.id, role: entry.role, name: entry.name })).catch(() => undefined);
      navigate(`/app/home/${entry.role}`);
    },
    [groupRoles, dispatch, navigate]
  );

  // A stored bell item (course versioning `program_updated`) is read once it is
  // opened: drop it locally so the list and the badge move at once, and tell
  // the BE (fire and forget, errors swallowed) so the next poll agrees.
  const dismissStoredNotification = useCallback(
    (notificationId: string) => {
      setNotifData((prev) => {
        if (!prev) return prev;
        const item = prev.notifications.find((n) => n.notificationId === notificationId);
        if (!item) return prev;
        const counted = STORED_COUNT[item.type];
        const summary = counted
          ? { ...prev.summary, [counted]: Math.max(0, (prev.summary[counted] ?? 0) - 1) }
          : prev.summary;
        return { ...prev, summary, notifications: prev.notifications.filter((n) => n !== item) };
      });
      if (!orgId) return;
      try {
        const tokens = localStorage.getItem('tokens') ? JSON.parse(localStorage.getItem('tokens') || '{}') : {};
        if (!tokens.id_token || !tokens.access_token) return;
        Promise.resolve(
          API.markMemberNotificationRead({
            orgId,
            notificationId,
            idtoken: tokens.id_token,
            authorization: tokens.access_token,
            location: userLocation
          })
        ).catch(() => undefined);
      } catch {
        /* storage blocked or request refused: the item comes back on the next poll at worst */
      }
    },
    [orgId, userLocation]
  );

  const notifBadgeCount = useMemo(() => {
    const s = notifData?.summary;
    if (!s) return 0;
    return (
      (s.unreadMessagesCount ?? 0) +
      (s.upcomingMeetingsCount ?? 0) +
      (s.pendingMatchesCount ?? 0) +
      (s.programUpdatesCount ?? 0) +
      (s.programAssignedCount ?? 0) +
      (s.programInvitesCount ?? 0) +
      (s.practiceNudgesCount ?? 0) +
      (s.memberNudgesCount ?? 0)
    );
  }, [notifData?.summary]);
  const hasNotificationBadge = notifBadgeCount > 0;

  const handleNotifOpen = (e: React.MouseEvent<HTMLElement>) => {
    setNotifAnchorEl(e.currentTarget);
  };
  const handleNotifClose = () => setNotifAnchorEl(null);
  const notifPopoverOpen = Boolean(notifAnchorEl);

  // The header renders on every app page, so polling here keeps the badge live
  // wherever the user is (new chat messages / upcoming calls).
  useEffect(() => {
    fetchNotifications();
    const interval = window.setInterval(fetchNotifications, 60_000);
    return () => window.clearInterval(interval);
  }, [fetchNotifications, selectedGroup?.id, selectedGroup?.role]);

  // The bell's panel: a popover on desktop, a bottom sheet on a phone.
  const notifPanel = (
    <>
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #2A2A2A' }}>
        <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
          Notifications
        </Typography>
      </Box>
      <Box sx={{ overflow: 'auto', flex: 1, py: 1, ...(notifLoading ? {} : fadeInSx) }}>
        {notifLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, px: 2, py: 1 }}>
            {[0, 1, 2].map((i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Bone width={22} height={22} radius="50%" />
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <Bone width="70%" height={12} />
                  <Bone width="45%" height={10} />
                </Box>
              </Box>
            ))}
          </Box>
        ) : !notifData?.notifications?.length ? (
          <Typography sx={{ color: '#868686', fontSize: 13, px: 2, py: 2 }}>
            No notifications
          </Typography>
        ) : (
          notifData.notifications.map((n, idx) => {
            const { path, grpId: targetGrpId, state: navState } = getNotificationNavigateTo(n);
            const isUnreadMessages = n.type === 'unread_messages';
            const isUpcomingMeetings = n.type === 'upcoming_meetings';
            const listIcon = isUnreadMessages ? (
              <img src={chatIcon} alt="" style={{ width: 20, height: 20, flexShrink: 0 }} />
            ) : isUpcomingMeetings ? (
              <CalendarMonthOutlinedIcon sx={{ color: '#B6B6B6', fontSize: 22, flexShrink: 0 }} />
            ) : null;
            return (
              <Box
                key={n.notificationId || `${n.type}-${idx}`}
                onClick={() => {
                  // Co-design invite: the admin dashboard opens that program's builder.
                  if (n.cta?.screen === 'program_build' && n.cta.programId) {
                    setOpenProgramIntent(n.cta.programId);
                  }
                  // Stored items (`program_updated`, `collaborator_invite`, `practice_nudge`, `member_nudge`) are read once opened.
                  if (n.notificationId) dismissStoredNotification(n.notificationId);
                  if (targetGrpId) openProgramFromNotification(targetGrpId);
                  else navigate(path, navState ? { state: navState } : undefined);
                  handleNotifClose();
                }}
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: idx < notifData.notifications.length - 1 ? '1px solid #2A2A2A' : 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' }
                }}
              >
                {listIcon}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ color: '#FDFDFD', fontSize: 13, fontWeight: 600, fontFamily: 'Inter, sans-serif', mb: 0.5 }}>
                    {n.title}
                  </Typography>
                  <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontFamily: 'Inter, sans-serif', lineHeight: 1.4 }}>
                    {n.text}
                  </Typography>
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </>
  );

  const logo = orgLogoResolved ? (
    <Box
      component="img"
      src={headerLogo}
      alt="Organization Logo"
      sx={{ maxWidth: 160, height: 36, objectFit: 'contain', ...fadeInSx, [MOBILE_SX]: { maxWidth: 76, height: 24 } }}
    />
  ) : (
    <Box sx={{ width: 36, height: 36 }} />
  );
  // Phone shell: back to the screen this one was opened from; straight to the
  // home tab when the app was opened on it directly (nothing to go back to).
  const goBack = () => (location.key !== 'default' ? navigate(-1) : navigate(homeRoute));

  return (
    <>
      <Box
        className={isMobileApp ? 'dosen-app-chrome' : undefined}
        sx={{
          height: 86,
          borderBottom: '1px solid #2A2A2A',
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: '#0F0F0F',
          // Phone: a 56px app bar under the status bar (the app paints under it when installed).
          [MOBILE_SX]: {
            flexShrink: 0,
            height: 'calc(56px + env(safe-area-inset-top))',
            pt: 'env(safe-area-inset-top)',
            pb: 0,
            px: 1,
            gap: 0.5
          }
        }}
      >
        {isMobileApp ? (
          // The menu lives in the bottom tabs; this corner holds back, or the org's logo on a main screen.
          <Box sx={{ width: 84, flexShrink: 0, display: 'flex', alignItems: 'center', pl: rootScreen ? 1 : 0 }}>
            {rootScreen ? (
              logo
            ) : (
              <IconButton aria-label="Back" data-testid="mobile-back" onClick={goBack} sx={{ color: '#FDFDFD' }}>
                <ArrowBackIosNewRoundedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            )}
          </Box>
        ) : (
          <IconButton onClick={onOpenMenu}>
            <MenuIcon sx={{ color: '#FDFDFD' }} />
          </IconButton>
        )}

        {/* One title style on every page: Inter 20/22, weight 400, uppercase, #FDFDFD, left-aligned.
            Phone: centred and smaller, wrapping to a second line rather than cutting a long title short. */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: '10px',
            [MOBILE_SX]: { minWidth: 0, justifyContent: 'center' }
          }}
        >
          <Box
            component="span"
            data-testid="app-header-title"
            sx={{
              color: '#FDFDFD',
              fontSize: 20,
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              textTransform: 'uppercase',
              lineHeight: '22px',
              [MOBILE_SX]: {
                fontSize: 13,
                lineHeight: '16px',
                fontWeight: 500,
                letterSpacing: '0.02em',
                textAlign: 'center',
                overflowWrap: 'anywhere',
                // Two lines fit the 56px bar; only a title longer than that is cut.
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }
            }}
          >
            {title}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            [MOBILE_SX]: { width: 84, flexShrink: 0, justifyContent: 'flex-end', gap: 1 }
          }}
        >
          {/* Elliott orb slot — dashboards portal their Elliott orb in here
              (HeaderElliottSlot), just left of the notifications bell. */}
          <Box id={HEADER_ELLIOTT_SLOT_ID} sx={{ display: 'flex', alignItems: 'center', '&:empty': { display: 'none' } }} />
          <Box sx={{ position: 'relative' }}>
            <DosenButton
              tone="basic"
              sizeVariant="sm"
              iconOnly
              onClick={handleNotifOpen}
              sx={{ minWidth: 40, width: 40, height: 40, p: 0, borderRadius: '50%', [MOBILE_SX]: { minWidth: 36, width: 36, height: 36 } }}
              aria-label="Notifications"
              aria-describedby={notifPopoverOpen ? 'notification-popover' : undefined}
              aria-haspopup="true"
            >
              <NotificationsOutlinedIcon sx={{ color: '#B6B6B6', fontSize: 22 }} />
            </DosenButton>
            {hasNotificationBadge && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -2,
                  right: -2,
                  minWidth: 16,
                  height: 16,
                  px: '4px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 10,
                  fontWeight: 700,
                  fontFamily: 'Inter, sans-serif',
                  color: '#FFFFFF',
                  background: 'linear-gradient(180deg, var(--orange-500, #F79420) 0%, var(--orange-700, #F15A26) 100%)',
                  border: '1px solid #0F0F0F',
                  pointerEvents: 'none'
                }}
              >
                {notifBadgeCount > 99 ? '99+' : notifBadgeCount}
              </Box>
            )}
          </Box>
          {!isMobileApp && logo}
        </Box>
      </Box>

      {isMobileApp ? (
        // Phone: the notifications slide up as a bottom sheet.
        <Drawer
          id="notification-popover"
          anchor="bottom"
          open={notifPopoverOpen}
          onClose={handleNotifClose}
          PaperProps={{
            sx: {
              bgcolor: '#0F0F0F',
              borderTop: '1px solid #2A2A2A',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: '85dvh',
              pb: 'env(safe-area-inset-bottom)',
              display: 'flex',
              flexDirection: 'column'
            }
          }}
        >
          <Box aria-hidden sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: '#444444', mx: 'auto', mt: 1 }} />
          {notifPanel}
        </Drawer>
      ) : (
        <Popover
          id="notification-popover"
          open={notifPopoverOpen}
          anchorEl={notifAnchorEl}
          onClose={handleNotifClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              bgcolor: '#0F0F0F',
              border: '1px solid #2A2A2A',
              borderRadius: '8px',
              mt: 1.5,
              minWidth: 320,
              maxWidth: 400,
              maxHeight: 'min(80vh, 480px)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }
          }}
        >
          {notifPanel}
        </Popover>
      )}
    </>
  );
}

/* ===========================================================
   WRAPPER — FUNDO PRETO + SCROLL INTERNO
   =========================================================== */
function ProgramDetailsWrapper({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const isMobileApp = useIsMobileApp();
  const [menuOpen, setMenuOpen] = useState(false);
  const [navRoutes, setNavRoutes] = useState<string[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const openMenu = useCallback(() => setMenuOpen(true), []);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const scrollToTop = useCallback(() => scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' }), []);

  // Phone shell: the page behind the app turns the app's dark (mobile-app.css).
  useEffect(() => {
    if (!isMobileApp) return undefined;
    document.documentElement.classList.add('dosen-app-shell');
    return () => document.documentElement.classList.remove('dosen-app-shell');
  }, [isMobileApp]);

  // Phone shell: a new screen starts at its top and fades in, like a pushed view.
  useEffect(() => {
    const el = scrollRef.current;
    if (!isMobileApp || !el) return;
    el.scrollTop = 0;
    el.classList.remove('dosen-page-enter');
    void el.offsetWidth; // restarts the animation
    el.classList.add('dosen-page-enter');
  }, [location.pathname, isMobileApp]);

  // Until the menu is known every screen counts as a main one, so the header never flashes a back button on load.
  const rootScreen = navRoutes.length === 0 || navRoutes.some((route) => isRootRoute(route, location.pathname));

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: '#0F0F0F',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Ahead of the header so its effects run in their old order; on a phone it also draws the bottom tabs. */}
      <ProgramSideDrawer
        open={menuOpen}
        onOpen={openMenu}
        onClose={closeMenu}
        onNavRoutes={setNavRoutes}
        onReselect={scrollToTop}
      />
      <ProgramHeader onOpenMenu={openMenu} rootScreen={rootScreen} homeRoute={navRoutes[0] || '/app/home/user'} />

      {/* Conteúdo com scroll */}
      <Box
        ref={scrollRef}
        className={isMobileApp ? 'dosen-app-scroll' : undefined}
        sx={{ flexGrow: 1, overflow: 'auto', padding: '25px', [MOBILE_SX]: { minHeight: 0, padding: '16px 16px 24px' } }}
      >
        {/* Not on full-screen task pages (builders, lessons): a pull there could reload over unsaved work. */}
        {isMobileApp && !isImmersiveRoute(location.pathname) && <PullToRefresh scrollRef={scrollRef} />}
        {children}
      </Box>
    </Box>
  );
}

const Layout = () => {
  // Intercom initialization
  const { firstName, lastName, email } = useProfileDetails();
  const location = useLocation();
  const selectedGroup = useAppSelector((state) => state.selectedGroup.data);
  const groupRole = selectedGroup.role || '';
  const module = useModule();
  const programType = module.isMentorship ? 'mentorship' : module.isMasterclass ? 'masterclass' : 'hybrid';
  const intercomAppId = process.env.REACT_APP_INTERCOMM_APP_ID;
  const intercomInitializedRef = useRef(false);
  const isMobileApp = useIsMobileApp();
  // Whether the launcher was hidden for a phone width, so a window back at desktop width shows it again.
  const launcherHiddenRef = useRef(false);

  const getIntercomAttributes = () => {
    const username = `${firstName || ''} ${lastName || ''}`.trim() || 'Unknown';
    return {
      name: username,
      email: email || undefined,
      'User Role': groupRole || undefined,
      'Program Module': programType || undefined,
      ...launcherSetting()
    };
  };

  // Phone: no floating launcher over the bottom tabs; Support in the menu opens the messenger. Desktop
  // never sends the setting (the workspace's own launcher setting stands) unless it was hidden here first.
  const launcherSetting = () => {
    if (isMobileApp) {
      launcherHiddenRef.current = true;
      return { hide_default_launcher: true };
    }
    if (launcherHiddenRef.current) {
      launcherHiddenRef.current = false;
      return { hide_default_launcher: false };
    }
    return {};
  };

  useEffect(() => {
    const initializeIntercom = async () => {
      if (intercomAppId) {
        try {
          const response = await API.intercomToken();
          if (response?.status === 200 && response?.data) {
            Intercom({
              app_id: intercomAppId,
              intercom_user_jwt: response.data,
              ...getIntercomAttributes(),
              session_duration: 86400000 // 1 day
            });
            intercomInitializedRef.current = true;
          }
        } catch (error) {
          console.warn('Failed to initialize Intercom:', error);
        }
      }
    };

    initializeIntercom();
  }, [firstName, lastName, email, intercomAppId, groupRole, programType]);

  useEffect(() => {
    if (!intercomAppId || !intercomInitializedRef.current) return;
    Intercom({
      app_id: intercomAppId,
      action: 'update',
      ...getIntercomAttributes()
    });
  }, [intercomAppId, location.pathname, location.search, groupRole, programType, firstName, lastName, email, isMobileApp]);

  return (
    <Box className="w-full h-full p-0" sx={{ height: '100% !important' }}>
      <MissingMandatoryFieldsTrigger />
      <ProgramDetailsWrapper>
        <Outlet />
      </ProgramDetailsWrapper>
    </Box>
  );
};

export default Layout;
